/* =====================================================================================
   What you can do with a result set once you have one.

   Export it, save the criteria under a name, and turn the results into accounts somebody owns.
   Kept in one component because these three belong together: they are the difference between a
   search that answers a question and a search that starts work.

   NOTHING HERE KNOWS WHAT A ROW IS. It takes columns, rows and a callback, so Circus or an
   orthopaedic tenant gets the same behaviour without this file learning any vocabulary.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import {
  downloadCsv, toCsv, listSavedSearches, saveSearch, deleteSavedSearch, type SavedSearch,
} from '../lib/resultActions';
import type { Criterion } from '../lib/search';

export function FilterActions({
  module, criteria, setCriteria, resultCount, exportName, exportColumns, exportRows, onAct, actLabel,
}: {
  module: string;
  criteria: Criterion[];
  setCriteria: (c: Criterion[]) => void;
  resultCount: number;
  exportName: string;
  exportColumns: { key: string; label: string }[];
  exportRows: () => Record<string, unknown>[];
  /** The domain action, e.g. create accounts. Absent means the button is not offered. */
  onAct?: () => Promise<string>;
  actLabel?: string;
}) {
  const [saved, setSaved] = useState<SavedSearch[]>([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = () => listSavedSearches(module).then(setSaved).catch(() => setSaved([]));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [module]);

  async function doSave() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await saveSearch(module, name, criteria, resultCount);
      setNaming(false); setName(''); setMsg('Saved.');
      await refresh();
    } catch (e) {
      setMsg(String((e as Error).message));
    } finally { setBusy(false); }
  }

  async function act() {
    if (!onAct) return;
    setBusy(true);
    try { setMsg(await onAct()); }
    catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fa">
      <div className="fa-row">
        <button className="btn" disabled={!resultCount}
                onClick={() => downloadCsv(exportName, toCsv(exportColumns, exportRows()))}>
          Export {resultCount.toLocaleString('en-GB')} to CSV
        </button>

        <button className="btn" disabled={!criteria.length} onClick={() => setNaming((v) => !v)}>
          Save this search
        </button>

        {onAct && (
          <button className="btn primary" disabled={!resultCount || busy} onClick={act}>
            {busy ? 'Working' : actLabel ?? 'Act on these'}
          </button>
        )}

        {saved.length > 0 && (
          <select className="input sel" value=""
                  onChange={(e) => {
                    const s = saved.find((x) => x.id === e.target.value);
                    if (s) setCriteria(s.criteria ?? []);
                  }}>
            <option value="">Open a saved search</option>
            {saved.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.result_count != null ? ` (${s.result_count} when saved)` : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {naming && (
        <div className="fa-row">
          <input className="input grow" placeholder="Name this search" value={name}
                 autoFocus
                 onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') doSave(); }} />
          <button className="btn primary" disabled={busy || !name.trim()} onClick={doSave}>
            Save
          </button>
          {/* Deleting is here rather than in the open menu, so choosing a saved search from a
              dropdown can never be one mis-click away from destroying it. */}
          {saved.length > 0 && (
            <select className="input sel" value=""
                    onChange={async (e) => {
                      if (!e.target.value) return;
                      await deleteSavedSearch(e.target.value);
                      setMsg('Deleted.');
                      await refresh();
                    }}>
              <option value="">Delete a saved search</option>
              {saved.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      )}

      {msg && <p className="fa-msg" role="status">{msg}</p>}
    </div>
  );
}
